import { CClient, showErrorAlert } from '@common/utils'
import useDetectCountry from '@hooks/useDetectCountry'
import { USER_COUNTRY_FROM_IP_COOKIE } from '@lib/constants/common'
import {
  COMPETITION_LIST_CSR_PAGE_SIZE,
  COMPETITION_TYPES,
} from '@lib/constants/competitions'
import { CompetititonsListType } from '@lib/types/competitions'
import { formatCountryQuery, getAPIErrorString } from '@lib/utils/common'
import { categorizeAndSortCompetitions } from '@lib/utils/competitions'
import { captureException } from '@sentry/node'
import { parseCookies } from 'nookies'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

type Options = {
  setCompetitions: Dispatch<
    SetStateAction<{
      upcomingCompetitions: CompetititonsListType
      liveCompetitions: CompetititonsListType
      completedCompetitions: CompetititonsListType
      practiceCompetitions: CompetititonsListType
    }>
  >
}

const useLazyLoadCompetitionList = ({ setCompetitions }: Options) => {
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [nextPage, setNextPage] = useState<string>(null)
  const [completedSectionListCount, setCompletedSectionListCount] = useState(0)

  const { countryCode: countryCodeFromHook } = useDetectCountry()

  const getCompletedCompetitions = async () => {
    setIsLoading(true)
    const countryCodeFromCookie = parseCookies()?.[USER_COUNTRY_FROM_IP_COOKIE]
    // Giving higher priority to country from the cookie as other
    // competitions are fetched using it.
    const countryQuery = formatCountryQuery(
      countryCodeFromCookie || countryCodeFromHook
    )
    const url = `/api/v1/competitions/?page_size=${COMPETITION_LIST_CSR_PAGE_SIZE}&competition_type=${COMPETITION_TYPES.CODING_CONTEST}&competition_type=${COMPETITION_TYPES.HACKATHON}&state=Completed${countryQuery}`
    try {
      const response = await CClient(url)

      if (!response.ok) throw new Error(await getAPIErrorString(response, url))

      const data = await response.json()

      if (data.next) setNextPage(data.next)
      if (data.count) setCompletedSectionListCount(data.count)

      const categorizedCompetitions = categorizeAndSortCompetitions(
        data.results
      )
      setCompetitions((prev) => ({
        ...prev,
        completedCompetitions: [
          ...prev.completedCompetitions,
          ...categorizedCompetitions.completed,
        ],
      }))
    } catch (error) {
      setHasError(true)
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching competitions. Please refresh the page and try again.',
      })
    }
    setIsLoading(false)
  }
  const handleLoadMore = async () => {
    setIsLoading(true)
    try {
      const response = await CClient(nextPage)

      if (!response.ok)
        throw new Error(await getAPIErrorString(response, nextPage))

      const data = await response.json()

      setNextPage(data.next || null)

      const categorizedCompetitions = categorizeAndSortCompetitions(
        data.results
      )
      setCompetitions((prev) => ({
        ...prev,
        completedCompetitions: [
          ...prev.completedCompetitions,
          ...categorizedCompetitions.completed,
        ],
      }))
    } catch (error) {
      setHasError(true)
      captureException(error)
      showErrorAlert({
        text:
          'Something went wrong while fetching competitions. Please refresh the page and try again.',
      })
    }
    setIsLoading(false)
  }
  const infiniteRef = useInfiniteScroll<HTMLDivElement>({
    loading: isLoading,
    hasNextPage: !hasError && !!nextPage,
    onLoadMore: handleLoadMore,
  })

  useEffect(() => {
    getCompletedCompetitions()
  }, [])

  return { infiniteRef, isLoading, completedSectionListCount }
}

export default useLazyLoadCompetitionList
